import React, { useState } from 'react'
import { Bell } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { useNotificationsContext } from '@/context/NotificationsContext'
import type { NotificationRecord } from '../services/notificationsApi'
import { NotificationSidePanel } from './NotificationSidePanel'

interface NotificationBellProps {
  notifications: NotificationRecord[]
  onDismiss: (id: string) => void
  onStartAction: (notification: NotificationRecord) => void
  onLeadAction: (notification: NotificationRecord, promptText: string) => void
}

// Just the trigger button (icon + unread badge) — the actual panel content
// (Alerts / Actions tabs) lives in NotificationSidePanel.tsx, a right-side
// drawer rather than the small anchored dropdown this used to open.
export const NotificationBell: React.FC<NotificationBellProps> = ({
  notifications,
  onDismiss,
  onStartAction,
  onLeadAction,
}) => {
  const { currentTheme } = useTheme()
  const { workspaceId } = useNotificationsContext()
  const [open, setOpen] = useState(false)

  const unreadCount = notifications.filter(
    (n) => n.status === 'unread' && (n.type === 'LEAD_ASSIGNED' || n.type === 'PROSPECT_QUALIFICATION_RESULT'),
  ).length

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        className="p-2 rounded-lg transition-all duration-300 hover:opacity-80"
        style={{
          position: 'relative',
          backgroundColor: currentTheme.secondary,
          color: currentTheme.text,
          border: `1px solid ${currentTheme.border}`,
        }}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              minWidth: '15px',
              height: '15px',
              borderRadius: '8px',
              background: '#EF4444',
              color: '#FFFFFF',
              fontSize: '9px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 3px',
              lineHeight: 1,
              pointerEvents: 'none',
            }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      <NotificationSidePanel
        open={open}
        onClose={() => setOpen(false)}
        workspaceId={workspaceId}
        notifications={notifications}
        onDismiss={onDismiss}
        onStartAction={onStartAction}
        onLeadAction={onLeadAction}
      />
    </>
  )
}
